import { ApolloError } from "@apollo/client";
import {
    IArea,
    IFunctionalLocation,
} from "../../pages/HMI/HMIDashboard/components/FailureDetails/FailureDetailsInterfaces";
import { IDetails } from "../../pages/HMI/HMIDashboard/components/Details/DetailsAPIInterfaces";
import { IUser } from "../UserContext/UserContextInterfaces";
import { ILastTwoShiftsEvents } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import { IClusterStore } from "contexts/ClusterCachedDataContext/ClusterCachedDataContextInterfaces";
import { ILanguage } from "components/LanguagesDropdown/LanguagesInterfaces";

export interface IContextState {
    store: IStore;
    dispatch: IDispatch;
}

export interface IStore {
    machineCenter?: IMachineCenter;
    failureTypes: IFailureType[];
    apolloQueryError?: ApolloError;
    isEventReportEditing: boolean | undefined;
    editEventReportData: {
        row: IDetails | undefined;
    };
    lastUpdated: Date | undefined;
    mqttBrokerSettings?: IMQTTBrokerSettings[];
    lastTwoShiftsEventReports: ILastTwoShiftsEvents | undefined;
    defaultUptimeValues?: IDefaultUptimeValues;
    editionsPending: IEventsEditionsPending;
    manualQualityPending: IManualQualityPending;
}

export interface IDispatch {
    GetData: (machine_center_id: number, login?: boolean) => Promise<void>;
    resetStore: () => void;
    editEventReportData: (row: IDetails) => void;
    isEventReportEditing: (isEditing: boolean) => void;
    handleMoveToCluster: (clusterStore: IClusterStore, machineCenterId: number) => void;
    trackEventEdition: (eventId: number, data: IEventsEditionsPending[0]) => void;
    /** Records a new pending manual-quality submission (called on Save click, before publish). */
    trackPendingManualQuality: (entry: IManualQualityPendingEntry) => void;
    /** Marks a pending entry as MQTT-acked (first gate). */
    markPendingManualQualityAcked: (key: string) => void;
    /** Drops a pending entry (used on explicit server rejection). */
    clearPendingManualQuality: (key: string) => void;
}

export interface IContextProps {
    children: React.ReactNode;
}

export type IFailureTypeWithoutFailureCodes = {
    failure_type_id: number;
    name: string;
    color_code: string;
    code?: string;
    is_location_specific: boolean;
    corporate_id?: number | null | string;
    default_failure_type_translations?: IDefaultFailureTypeTranslations[];
};

export type IFailureType = IFailureTypeWithoutFailureCodes & { failure_codes: IFailureCode[] };

export interface IFailureCode {
    failure_code_id: number;
    name: string;
    code?: string;
    primary_code_id?: number | null;
    corporate_id?: number | null;
    failure_type_location_specific?: boolean | null;
    default_failure_code_translations?: IDefaultFailureCodeTranslations[];
}

export interface IDefaultFailureTypeTranslations {
    failure_type_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IDefaultFailureCodeTranslations {
    failure_code_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IDefaultKeySuccessFactorTranslations {
    key_success_factor_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IDefaultKeyMeasureMetricTranslations {
    measure_metric_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IDefaultUserTypeTranslations {
    user_type_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IDefaultStatusTranslations {
    status_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IDefaultPriorityTranslations {
    priority_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IDefaultRCAFrequencyTriggerUnitTranslations {
    rca_frequency_trigger_unit_id: number;
    language: ILanguage;
    translated_name: string;
}

export interface IUserOperator {
    first_name: string;
    last_name: string;
    user_id: number;
}

export interface IMQTTBrokerSettings {
    allow_anonymous: boolean;
    id: string;
    host: string;
    name: string;
    user?: string;
    password?: string;
}

export interface IDefaultUptimeValues {
    no_operator_id?: number;
    not_selected_type_id?: number;
}

export type IMQTTBrokerSettingWithoutId = Omit<IMQTTBrokerSettings, "id">;

export interface IHMIMill {
    mill_id: number;
    cluster_enabled: boolean;
    country: ICountry;
    dedicated_localserver?: boolean;
    mill_modules?: {
        is_enabled: boolean;
    }[];
    mill_data: {
        timezone: string;
    };
    corporate: {
        country: ICountry;
    };
    mqtt_broker_settings: IMQTTBrokerSettings[];
}
export interface IProductionLine {
    production_line_shifts: { shift_id: number }[];
    name?: string;
    mill: IHMIMill;
}

export interface ICountry {
    code: string;
}

export interface IShift {
    shift: {
        shift_id: number;
        start_time: string;
        end_time: string;
    };
}

export interface IMachineCenter {
    machine_center_id: number;
    downtime_method_capture_id: number;
    plc_address?: string;
    name: string;
    hmi_code: string;
    areas: IArea[];
    users: IUser[];
    minimum_downtime: number;
    downtime_trigger_threshold: number;
    production_line_id?: number;
    production_line: IProductionLine;
    machine_center_modules?: {
        is_enabled: boolean;
    }[];
    machine_center_oee_config?: {
        record_quality_enabled: boolean;
        quality_manual_entry: boolean;
    };
    cluster?: {
        inactivity_time_limit: number;
        inactivity_alert_delay: number;
    };
}

export interface IEventReport {
    event_report_id: number;
    end_date: Date;
    start_date: Date;
    failure_type: IFailureTypeWithoutFailureCodes;
    measure_metric_id: number;
    area: IArea;
    functional_location: IFunctionalLocation;
    failure_code: IFailureCode;
    user_operator: IUserOperator;
    notes?: string;
}

export interface IEventsEditionsPending {
    [eventId: number]: {
        machineCenterId: number;
        failureTypeDetails?: IFailureType;
        failureCodeDetails?: IFailureCode;
        areaDetails?: IArea;
        functionalLocationDetails?: IFunctionalLocation;
        lastUpdateDate: Date;
    };
}

export interface IManualQualityPendingEntry {
    machineCenterId: number;
    /** Hour-aligned ISO timestamp matching `oee_quality_calculation_summary.interval_start`. */
    hourAlignedIntervalStartIso: string;
    /** Operator-submitted scrap value. */
    value: number;
    /** Idempotency key carried through the MQTT round trip. */
    uuid: string;
    /** Epoch ms when the operator clicked Save. */
    submittedAt: number;
    /** First gate: local-server has acked this submission via MQTT. */
    mqttAcked: boolean;
    /**
     * Second gate: a data sync has run since `mqttAcked` flipped true.
     * When both gates are met, the entry is cleared from the map.
     */
    syncObserved: boolean;
}

/** Map keyed by `${mcId}:${hourAlignedIntervalStartIso}`. */
export interface IManualQualityPending {
    [key: string]: IManualQualityPendingEntry;
}

export const buildManualQualityPendingKey = (mcId: number, hourAlignedIntervalStartIso: string): string =>
    `${mcId}:${hourAlignedIntervalStartIso}`;
